import React, { type ReactNode, useMemo } from 'react';

import { cssMap } from '@atlaskit/css';
import type {
	AnyEntryPoint,
	EntryPointRef,
	RuntimePropsOfEntryPoint,
} from '@atlassian/entry-point-types';
import {
	type ErrorFallback,
	InternalEntryPointContainer,
} from '@atlassian/internal-entry-point-container';
import {
	PreviewPanel,
	PreviewPanelBody,
	PreviewPanelHeader,
	PreviewPanelTitle,
} from '@atlassian/preview-panel';
import { usePreviewPanelsActions } from '@atlassian/preview-panels-api';
import type { JSResourceReference } from '@atlassian/react-async';

type Props<TEntryPoint> = {
	url: string;
	name: string;
	icon: ReactNode;
	entryPoint: TEntryPoint;
	preloadedEntryPointReference: EntryPointRef<TEntryPoint>;
	fallback?: ReactNode;
	errorFallback?: ErrorFallback;
};

const emptyEntryPointProps = {} as RuntimePropsOfEntryPoint<AnyEntryPoint>;

const styles = cssMap({
	body: {
		display: 'flex',
		flexGrow: 1,
		overflowY: 'auto',
	},
});

export const EntryPointPreviewPanel = <TEntryPoint extends AnyEntryPoint>({
	url,
	name,
	icon,
	entryPoint,
	preloadedEntryPointReference,
	fallback,
	errorFallback,
}: Props<TEntryPoint>) => {
	const { close } = usePreviewPanelsActions();

	const id = useMemo((): string => {
		return (entryPoint.root as JSResourceReference<any>).getModuleName() || 'unknown';
	}, [entryPoint]);

	return (
		<PreviewPanel>
			<PreviewPanelHeader onClose={close} url={url}>
				<PreviewPanelTitle title={name} icon={icon} />
			</PreviewPanelHeader>
			<PreviewPanelBody styles={styles.body}>
				<InternalEntryPointContainer
					id={id}
					entryPointReference={preloadedEntryPointReference}
					fallback={fallback}
					errorFallback={errorFallback}
					runtimeProps={emptyEntryPointProps}
				/>
			</PreviewPanelBody>
		</PreviewPanel>
	);
};
